import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { Tab, Nav, NavItem } from "react-bootstrap";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import utils from "@utils";
import { JOB_INVOICED_TYPES } from "@constants";
import { ClientService, ClientDocumentsService, ContactService, OrganizationService, JobService } from "@services";

import { UploadTaxDocument, InviteClientModal } from "../components";
import {
	ReactSelect,
	SelectBadges,
	Loader,
	SearchField,
	AddContactModal,
	ConfirmationModal,
	SchedulingOnlyOptionsModal,
} from "../../../components";

import "./ClientForm.scss";

class ClientForm extends Component {
	constructor(props) {
		super(props);
		const { lang } = global;
		const {
			router: {
				params: { clientId },
			},
		} = props;

		this.getAgencyRules();
		this.getPaymentRules();
		this.state = {
			pageTitle: clientId ? lang.t("Edit Client") : lang.t("Add Client"),
			editMode: clientId !== undefined,
			TaxExemptionRequest: null,
			showModal: false,
			tempContacts: [],
			contactConflicts: [],
			mergedContacts: [],
			cancelClicked: false,
			client: {
				loading: false,
				data: clientId ? { Id: clientId } : {},
			},
			contacts: {
				loading: false,
				data: [],
				options: [],
				search: "",
			},
			tableContacts: [],
			form: {
				Name: "",
				PrimaryContactId: "",
				EmergencyContactId: "",
				BillingContactId: "",
				IsAgencyRKPayEnabled: false,
				IsActive: "true",
				OrganizationFee: 0,
				ClientCoordinatorIds: [],
				clientDocuments: [],
				JobInvoicedTypeId: null,
				AuthorizedToUseDepartmentVehicles: false,
				AllowExcludeOfficerRate: false,
				TransferOfficerPaymentsToAgency: false,
			},
			validation: {
				Name: false, // true = has error
				BillingContactId: false,
			},
			clientCoordinators: [],
			changeCoordinators: false,
			isAgencyClient: false,
			showInviteClientModal: false,
			agencyRules: {
				OnlyAuthorizedClientsAreAllowedToUseVehicles: false,
				RequireInvoicingForVehicles: false,
			},
			agencyPaymentRules: {
				TransferPaymentToJurisdiction: false,
				TransferPaymentsToAgencyOption: false,
			},
			showAllowExcludeOfficerRate: false,
			showSchedulingOnlyOptionsModal: false,
			schedulyOnlyOptions: null,

		};
		const {
			flags: { mergeContactFix },
		} = props;
		this.mergeContactFixEnabled = !!mergeContactFix;
	}

	componentDidMount() {
		const { editMode } = this.state;
		if (editMode) {
			this.getClient();
			this.getContacts();
			this.getClientDocument();
		} else this.getClientCoordinators();
	}

	getAgencyRules = () => {
		const { lang } = global;
		const { user } = this.props;

		if (user.isLEA()) {
			const promises = [OrganizationService.getJurisdictionAgencyRules()];
			Promise.all(promises)
				.then(([{ data: agencyRules }]) => {
					this.setState(() => ({
						agencyRules,
					}));
				})
				.catch(() =>
					utils.notify({
						type: "error",
						message: lang.t("Error getting Jurisdiction Agency Rules"),
						timeOut: 1500,
					}),
				);
		}
	};

	getPaymentRules = () => {
		const { lang } = global;
		const { user } = this.props;

		if (user.isLEA()) {
			const promises = [OrganizationService.getJurisdictionPaymentRules()];
			Promise.all(promises)
				.then(([{ data: agencyPaymentRules }]) => {
					this.setState(() => ({
						agencyPaymentRules,
					}));
				})
				.catch(() =>
					utils.notify({
						type: "error",
						message: lang.t("Error getting Jurisdiction Agency Rules"),
						timeOut: 1500,
					}),
				);
		}
	};

	/** Retrieve details for the client */
	getClient = () => {
		const { lang } = global;
		const {
			client: {
				data: { Id },
			},
		} = this.state;

		this.setState(state => ({
			client: { ...state.client, loading: true },
		}));

		ClientService.getById(Id)
			.then(({ data }) => {
				this.setClientForm(data);
			})
			.catch(() => {
				this.setState(state => ({
					client: { ...state.client, loading: false },
				}));

				utils.notify({
					type: "error",
					message: lang.t("Error loading client information"),
				});
			});
	};

	/** Retrieve a comprehensive list of contacts for the client (non-paginated) */
	getContacts = () => {
		const {
			client: {
				data: { Id },
			},
		} = this.state;
		this.setState(state => ({
			contacts: { ...state.contacts, loading: true },
		}));

		ContactService.getAll({ clientId: Id })
			.then(({ data }) => {
				const options = data.ItemList.map(item => ({
					value: item.Id,
					label: item.ContactName,
				}));

				this.setState({
					contacts: { data: data.ItemList, loading: false, options },
					tableContacts: [...data.ItemList],
				});
			})
			.catch(() => {
				const { lang } = global;

				utils.notify({
					type: "error",
					message: lang.t("Error loading client contacts"),
				});

				this.setprevState(state => ({
					contacts: { ...state.contacts, loading: false },
				}));
			});
	};

	/** Retrieves the list of LEA coordinators that are allowed to work with the client */
	getClientCoordinators = () => {
		const { lang } = global;
		const {
			user: {
				Organization: { Id },
				Id: userId,
				IsInternalCoordinator,
			},
		} = this.props;
		const { editMode } = this.state;
		// if internal coord + id exists in coord list allow edit

		ContactService.getAll({ clientId: Id, isOrganization: true })
			.then(({ data: { ItemList } }) => {
				// create an array of potential coordinators who have User Ids
				const contacts = ItemList.map(({ UserId: value, ContactName: label }) => ({
					value,
					label,
				})).filter(item => item.value !== 0 || !!item.value);

				this.setState(prevState => {
					let ClientCoordinatorIds = prevState.form.ClientCoordinatorIds.map(id => {
						if (contacts.some(item => id.value === item.value)) {
							return contacts.find(item => item.value === id.value);
						}
						return { ...id, label: `user-${id.value}` };
					});
					const currentUser = contacts.find(item => item.value === userId);
					if (currentUser && !editMode && IsInternalCoordinator) {
						ClientCoordinatorIds = [currentUser];
					}
					return {
						clientCoordinators: contacts,
						form: {
							...prevState.form,
							ClientCoordinatorIds,
						},
						internalCoordinatorIncluded: editMode
							? ClientCoordinatorIds.some(item => item.value === userId) &&
								ClientCoordinatorIds.length === 1
							: true,
					};
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading client coordinators"),
					timeOut: 1500,
				});
			});
	};

	/**
	 * Updates `this.state` with client details
	 * @param {Object} data Information to update state with
	 */
	setClientForm = data => {
		this.setState(
			prevState => ({
				client: { loading: false, data },
				TaxExemptionRequest: data.TaxExemptionRequest,
				showAllowExcludeOfficerRate: data.AuthorizedToUseDepartmentVehicles,
				form: {
					...prevState.form,
					Name: data.Name,
					PrimaryContactId: data.PrimaryContactId ?? "",
					EmergencyContactId: data.EmergencyContactId ?? "",
					BillingContactId: data.BillingContactId ?? "",
					IsAgencyRKPayEnabled: data.IsAgencyRKPayEnabled,
					ClientInvoiceDisclaimer: data.ClientInvoiceDisclaimer,
					IsActive: data.IsActive ? "true" : "false",
					OrganizationFee: data.OrganizationFee,
					JobInvoicedTypeId: data.JobInvoicedTypeId,
					ClientCoordinatorIds: data.ClientCoordinatorIds.map(value => ({ value })) ?? [],
					AuthorizedToUseDepartmentVehicles: data.AuthorizedToUseDepartmentVehicles,
					AllowExcludeOfficerRate: data.AllowExcludeOfficerRate,
					TransferOfficerPaymentsToAgency: data.TransferOfficerPaymentsToAgency,
				},
			}),
			this.getClientCoordinators,
		);
	};

	/**
	 * Updates which contacts are selectable from dropdowns for a client that has not yet been created
	 * @param {Object} rawContact Contact information to process and add to/merge with the current list of contacts
	 */
	updateContactsForNewClient = rawContact => {
		const { tempContacts } = this.state;

		const newContact = { ...rawContact };

		let newTempContacts = [];

		if (!newContact.Id) {
			newContact.Id = `newContact_${tempContacts.length}`;
			newTempContacts = [...tempContacts, newContact];
		} else {
			newTempContacts = tempContacts.map(item =>
				item.Id === newContact.Id ? newContact : item,
			);
		}

		const options = newTempContacts.map(item => ({
			value: item.Id,
			label: `${item.LastName}, ${item.FirstName}`,
		}));

		this.setState(state => ({
			contacts: { ...state.contacts, options },
			tempContacts: newTempContacts,
		}));
	};

	/**
	 * Updates which contacts are selectable from dropdowns for an existing client
	 * @param {Object} obj
	 * @param {Object} obj.data Contact information to process and add to/merge with the current list of contacts
	 */
	updateContactsForExistingClient = ({ data: rawContact }) => {
		const { contacts } = this.state;
		let newContacts = [];
		const existingContact = contacts.data.find(item => item.Id === rawContact.Id);
		if (existingContact) {
			newContacts = contacts.data.map(item =>
				item.Id === rawContact.Id ? rawContact : item,
			);
		} else {
			newContacts = [...contacts.data, rawContact];
		}

		const options = newContacts.map(item => ({
			value: item.Id,
			label: `${item.LastName}, ${item.FirstName}`,
		}));

		this.setState(state => ({
			contacts: {
				...state.contacts,
				options,
			},
		}));
	};

	/** Creates a new client */
	createClient = async () => {
		const { lang } = global;
		const { form, tempContacts, schedulyOnlyOptions } = this.state;
		const { user, router } = this.props;
		const ClientCoordinatorIds = form.ClientCoordinatorIds.map(({ value }) => value);
		const params = {
			...form,
			PrimaryContactId: "",
			EmergencyContactId: "",
			BillingContactId: "",
			ClientCoordinatorIds,
		};
		const queryParams = {};

		// If creating a client from a Coordinator user, create its	default project
		if (user.isCoordinator()) queryParams.createDefaultProject = true;

		this.setState(state => ({
			client: { ...state.client, loading: true },
		}));

		try {
			const { data } = await ClientService.save(params, queryParams);

			const processContacts = tempContacts.length > 0;

			this.setState({
				client: { data, loading: processContacts },
			});

			if (processContacts) {
				await this.processTempContacts();
			} else if (schedulyOnlyOptions !== null) {
				await JobService.upsertSchedulingOnlyOptions({
					...schedulyOnlyOptions,
					ClientId: data.Id,
				});
			}

			utils.notify({
				type: "success",
				message: `${lang.t("Client successfully")} ${lang.t("Created")}`,
			});
			if (!this.mergeContactFixEnabled)
				router.push("/clients");
		} catch ({ response }) {
			utils.notify({
				type: "error",
				message: response.data.Message ? response.data.Message : "Error creating client",
			});
		} finally {
			this.setState(state => ({
				client: { ...state.client, loading: false },
			}));
		}
	};

	/**
	 * Handles any temporary contact records that exist after creating a client.
	 * Each contact record is created and attached to the new client.
	 * Once onCancel is clicked this function is called when the user clicks save again.
	 * If the user has added more contacts then they are sent back to Processing those contacts using this.processTempContacts
	 */
	processMergeCancel = () => {
		const { tempContacts, cancelContact } = this.state;

		if (
			tempContacts.length === 1 &&
			tempContacts[0].email === cancelContact.Data.EmailAddress
		) {
			this.updateFormIncorrectContact();
		} else if (
			tempContacts.length === 1 &&
			tempContacts[0].id === cancelContact.tempId &&
			tempContacts[0].email !== cancelContact.Data.EmailAddress
		) {
			this.addContactPlusUpdate();
		} else {
			this.processTempContacts();
		}

		this.setState({ cancelClicked: false });
	};

	// To update the form if the user does not change their email address and saves the form
	updateFormIncorrectContact = () => {
		const { form, tempContacts, client } = this.state;

		if (Object.values(form).includes(tempContacts[0].id)) {
			this.setState(
				prevState => ({
					form: {
						...prevState.form,
						PrimaryContactId: client?.data?.PrimaryContactId || "",
						BillingContactId: client?.data?.BillingContactId || "",
						EmergencyContactId: client?.data?.EmergencyContactId || "",
					},
				}),
				() => {
					this.updateClient();
				},
			);
		} else {
			this.updateClient();
		}
	};

	// Function used to create contact and update the client with the correct contact information, if a single contact email was changed after onCancel;
	addContactPlusUpdate = () => {
		const { lang } = global;
		const { tempContacts, client } = this.state;

		const contact = tempContacts[0];
		contact.clientId = client.data.Id;

		ContactService.createContact(contact)
			.then(({ data }) => {
				const newContact = {
					...data,
					tempId: contact.id,
				};

				this.setNewClientContacts([newContact]);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error creating contact"),
				});
			});
	};

	updateClient = async (stay = false) => {
		const {
			router: { push },
		} = this.props;
		const { editMode, client, form, schedulyOnlyOptions, mergedContacts } = this.state;
		const { lang } = global;

		const ClientCoordinatorIds = form.ClientCoordinatorIds.map(({ value }) => value);
		const newForm = {
			...form,
			Id: client.data.Id,
			ClientCoordinatorIds,
		};

		if (schedulyOnlyOptions !== null && mergedContacts?.length > 0) {
			const SchedulingBillingContactId =
				mergedContacts.find(
					item => item.tempId === schedulyOnlyOptions.SchedulingBillingContactId,
				)?.Id ?? null;
			await JobService.upsertSchedulingOnlyOptions({
				...schedulyOnlyOptions,
				SchedulingBillingContactId,
				ClientId: client.data.Id,
			});
		}

		await ClientService.save(newForm)
			.then(() => {
				utils.notify({
					type: "success",
					message: `${lang.t("Client successfully")} ${editMode ? lang.t("Updated") : lang.t("Created")}`,
				});

				if (stay) this.setClientForm(newForm);
				else push("/clients");
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating client"),
				});

				this.setState(state => ({
					client: { ...state.client, loading: false },
					contacts: { ...state.contacts, loading: false },
				}));
			});
	};

	processTempContacts = async () => {
		const { tempContacts, client } = this.state;
		const { lang } = global;

		const contactPromises = tempContacts.map(
			({ Id: tempId, ...item }) =>
				new Promise(resolve => {
					const contact = { ...item, tempId, clientId: client.data.Id };
					ContactService.createContact(contact)
						.then(({ data }) => {
							resolve({
								...data,
								tempId,
								error: false,
							});
						})
						.catch(({ response }) => {
							const { data } = response;

							// Use resolve instead of reject to make Promise.all complete all the promises, but set error=true
							resolve({
								...data,
								tempId,
								error: true,
								status: response.status,
							});
						});
				}),
		);

		await Promise.all(contactPromises).then(results => {
			const merged = [];
			const conflicts = [];

			results.forEach(item => {
				// Since contact promises always resolve, use "error" variable to tell if the api request returned success or error
				if (!item.error) {
					const { error, ...props } = item;
					merged.push({ ...props });
				} else if (item.status === 409) {
					const { error, ...props } = item;
					conflicts.push({
						...props,
						Message:
							item.Message ||
							`${lang.t("A contact with this email already exists")}`` ${lang.t("A contact with this email already exists")}`,
					});
				} else {
					utils.notify({
						type: "error",
						message: item.Message || lang.t("Error creating contact"),
					});
				}
			});

			this.setState(
				{
					mergedContacts: [...merged],
					contactConflicts: conflicts,
				},
				() => {
					if (conflicts.length > 0) {
						this.processContactConflicts();
					} else {
						this.setNewClientContacts(merged);
					}
				},
			);
		});
	};

	/**
	 * If a contact cannot be created due to another contact having the same email address, the user is informed and
	 * prompted to merge the two contacts. If they agree to do so, the two contact records are merged.
	 *
	 * If there are no conflicts, the primary, emergency, and billing contacts are set for the client.
	 */
	processContactConflicts = () => {
		const { lang } = global;
		const { contactConflicts, client, mergedContacts } = this.state;
		const { router } = this.props;

		if (contactConflicts.length > 0) {
			const currentConflict = contactConflicts[0];
			const {
				Data: { ContactName, EmailAddress },
			} = currentConflict;

			const customMessage = lang.t("dynamic.A contact with email already exists", {
				EmailAddress,
			});
			const customMessage2 = lang.t(
				"Do you want to associate the client with this existing contact?",
			);
			const message = (
				<div>
					{customMessage} <br /> {ContactName} <br /> {customMessage2}
				</div>
			);

			this._confirmationModal.openModal({
				title: lang.t("Merge Contact"),
				message,
				confirmationButton: "Yes",
				onConfirm: () => {
					this.mergeContact(
						currentConflict.Data,
						currentConflict.tempId,
						this.processContactConflicts,
						() => {
							if (this.MergeContactFixEnabled)
                    			router.push("/clients");
              }
					);
				},
				onCancel: () => {
					const remainingConflicts = contactConflicts.filter(
						({ Data }) => Data.EmailAddress !== EmailAddress,
					);

					this.setState(
						{
							cancelContact: contactConflicts[0],
							cancelClicked: true,
							contactConflicts: remainingConflicts,
							client: {
								loading: false,
								data: { ...client.data },
							},
						},
						() => {
							this.processContactConflicts();
						},
					);
				},
			});
		} else if (mergedContacts.length > 0) {
			this.setNewClientContacts(mergedContacts);
		}
	};

	/**
	 * Sets the primary, emergency, and billing contacts for the client if they have been specified
	 * @param {Array} newContacts Up-to-date array of contacts
	 */
	setNewClientContacts = newContacts => {
		const { form } = this.state;
		const newForm = { ...form };

		if (newContacts.length > 0) {
			const primaryContact = newContacts.find(item => item.tempId === form.PrimaryContactId);
			const emergencyContact = newContacts.find(
				item => item.tempId === form.EmergencyContactId,
			);
			const billingContact = newContacts.find(item => item.tempId === form.BillingContactId);

			newForm.PrimaryContactId = primaryContact?.Id ?? "";
			newForm.EmergencyContactId = emergencyContact?.Id ?? "";
			newForm.BillingContactId = billingContact?.Id ?? "";
		}

		this.setState(
			{
				form: newForm,
			},
			() => {
				this.updateClient();
			},
		);
	};

	handleAuthorizedToUseDepartmentVehicles = property => () => {
		this.setState(prevState => ({
			form: {
				...prevState.form,
				[property]: !prevState.form[property],
				AllowExcludeOfficerRate: false,
			},
			showAllowExcludeOfficerRate: !prevState.showAllowExcludeOfficerRate,
		}));
	};

	handleAllowExcludeOfficerRate = property => () => {
		this.setState(prevState => ({
			form: {
				...prevState.form,
				[property]: !prevState.form[property],
			},
		}));
	};

	handleTransferOfficerPaymentsToAgency = property => () => {
		this.setState(prevState => ({
			form: {
				...prevState.form,
				[property]: !prevState.form[property],
			},
		}));
	};

	onSaveClick = () => {
		const { form, editMode, cancelClicked } = this.state;
		const { user } = this.props;
		const { Name, BillingContactId } = form;
		const billingContactIdError =
			(form.JobInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING ||
				(form.JobInvoicedTypeId === JOB_INVOICED_TYPES.AGENCY_INVOICING &&
					user.isInvoicingV3())) &&
			!BillingContactId;
		form.IsAgencyRKPayEnabled = form.JobInvoicedTypeId === 1;
		const nameError = Name === "";

		if (nameError || billingContactIdError) {
			this.setState({
				validation: {
					Name: nameError,
					BillingContactId: billingContactIdError,
				},
			});

			return;
		}

		if (editMode) {
			this.updateClient();
		} else if (cancelClicked) {
			this.processMergeCancel();
		} else {
			this.createClient();
		}
	};

	/**
	 * Merges a new contact record with an existing contact record based on a shared email address
	 * @param {Object} obj
	 * @param {Number} obj.Id The ID number of the existing contact record
	 * @param {String} obj.EmailAddress The email address shared by the two contact records
	 * @param {Number|String} tempId The temporary Id assigned to the newer contact record
	 * @param {Function} callback An optional callback method called after contact merging is complete
	 */
	mergeContact = ({ Id: contactId, EmailAddress }, tempId, callback) => {
		const { lang } = global;
		const { client, mergedContacts, contactConflicts } = this.state;
		const params = { Id: contactId, clientId: client.data.Id };

		const remainingConflicts = contactConflicts.filter(
			({ Data }) => Data.EmailAddress !== EmailAddress,
		);

		ContactService.mergeContact(params)
			.then(({ data }) => {
				const newContact = { ...data, tempId };
				this.setState(
					{
						mergedContacts: [...mergedContacts, newContact],
						contactConflicts: remainingConflicts,
					},
					callback,
				);
			})
			.catch(error => {
				const { response } = error;

				utils.notify({
					type: "error",
					message: response.data.Message || lang.t("Unable to merge contact"),
				});

				this.setState({ contactConflicts: remainingConflicts }, callback);
			});
	};

	/**
	 * Uploads a tax exemption document on behalf of the client
	 * @param {Event} event The input element containing the file to be uploaded
	 */
	uploadDocument = event => {
		const file = event?.target?.files[0] ?? null;

		if (!file) return;

		const { lang } = global;
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			const { client } = this.state;
			ClientService.uploadTaxExemptionDocument(client.data.Id, file)
				.then(({ data: { Message } }) => {
					utils.notify({
						message: Message,
						type: "success",
						title: lang.t("Upload Successful"),
					});

					this.getClient();
				})
				.catch(() => {
					utils.notify({
						message: lang.t("Error uploading document"),
						type: "error",
					});
				});
		};
	};

	onUploadClick = () => {
		this._uploadInput.click();
	};

	clientDocumentClick = () => {
		this.clientDocInput.click();
	};

	/** Retrieves all documents uploaded for this client. This does _NOT_ include tax exemption documentation. */
	getClientDocument = () => {
		const { client } = this.state;
		const { lang } = global;
		ClientDocumentsService.getClientDocument(client.data.Id)
			.then(({ data }) => {
				this.setState(prevState => ({
					form: {
						...prevState.form,
						clientDocuments: [...data],
					},
				}));
			})
			.catch(() => {
				utils.notify({
					message: lang.t("Error uploading document"),
					type: "error",
				});
			});
	};

	/**
	 * Uploads a document and associates it with the client record
	 * @param {Event} event The input containing the file to be uploaded
	 */
	uploadClientDocument = event => {
		event.preventDefault();
		const input = event.target;
		const { lang } = global;
		const { client } = this.state;
		if (!input.files || !input.files[0]) return;
		if (input.files[0].size / (1024 * 1024) > 100) {
			utils.notify({
				message: lang.t("File exceeds the maximum acceptable size"),
				type: "error",
			});
			return;
		}
		const reader = new FileReader();
		reader.readAsDataURL(input.files[0]);
		reader.onload = () => {
			ClientDocumentsService.uploadClientDocument(client.data.Id, input.files[0])
				.then(() => {
					utils.notify({
						type: "success",
						title: lang.t("Upload Successful"),
					});
					this.getClientDocument();
				})
				.catch(() => {
					utils.notify({
						message: lang.t("Error uploading document"),
						type: "error",
					});
				});
			// value needs to be cleared for reuploading of docs
			input.value = "";
		};
	};

	/**
	 * Deletes an existing document from the client record
	 * @param {Number} documentId The ID number for the document to be deleted
	 */
	removeClientDocument = documentId => {
		const { lang } = global;
		const { client } = this.state;
		ClientDocumentsService.deleteClientDocument(client.data.Id, documentId)
			.then(() => {
				this.setState(prevState => ({
					form: {
						...prevState.form,
						clientDocuments: prevState.form.clientDocuments.filter(
							item => item.Id !== documentId,
						),
					},
				}));
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error deleting document"),
				});
			});
	};

	/**
	 * Prompts the user with a confirmation modal when they choose to delete a client document
	 * @param {Object} item The document object the user has chosen to delete
	 */
	onDeleteDocument = item => {
		const { lang } = global;
		this._confirmationModal.openModal({
			title: lang.t("Delete Document Confirmation"),
			message: lang.t("dynamic.Are you sure you want to delete", {
				subject: `${item.FileName}`,
			}),
			confirmButton: lang.t("Yes"),
			onConfirm: () => {
				this.removeClientDocument(item.Id);
			},
		});
	};

	onAddContact = () => {
		this.setState({
			showModal: true,
		});
	};

	onCloseContact = () => {
		this.setState({
			showModal: false,
			contact: {},
		});
	};

	/**
	 * Opens the modal allowing user to edit contact details. For existing contacts, details are retrieved from the API
	 * @param {'PrimaryContactId'|'EmergencyContactId'|'BillingContactId'} type Indicates the type of the contact
	 * @param {Number} rawContactId The ID number of the contact
	 */
	onEditContact = (type, rawContactId) => event => {
		if (event) {
			const {
				target: {
					dataset: { preventNavigation },
				},
			} = event;
			if (preventNavigation) {
				return;
			}
		}

		const { form, tempContacts, client } = this.state;
		let contactId = rawContactId;

		if (type !== null) {
			contactId = form[type];

			if (contactId.toString().indexOf("newContact_") !== -1) {
				const contact = tempContacts.find(item => item.id === contactId);
				this._contactModal.openModal(contact);
				return;
			}
		}
		this.setState(() => ({
			client: { ...client, loading: true },
		}));

		ContactService.getContact(contactId)
			.then(({ data }) => {
				this.setState(() => ({
					contact: data,
					showModal: true,
					client: { ...client, loading: false },
				}));
			})
			.catch(() => {
				const { lang } = global;

				utils.notify({
					type: "error",
					message: lang.t("Error loading contact information"),
				});

				this.setState(() => ({
					client: { ...client, loading: false },
				}));
			});
	};

	/**
	 * Prompts the user with a confirmation modal when they choose to delete a contact
	 * @param {Number} contactId The ID number for the contact
	 */
	onDeleteContact = contactId => event => {
		if (event) event.preventDefault();

		const { lang } = global;
		const { client, form } = this.state;

		this._confirmationModal.openModal({
			title: lang.t("Delete Contact"),
			message: lang.t("Are you sure you want to delete this contact_"),
			confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Contact")}`,
			onConfirm: () => {
				ContactService.deleteClientContact(client.data.Id, contactId)
					.then(() => {
						this.setState(prevState => ({
							contacts: {
								...prevState.contacts,
								data: prevState.contacts.data.filter(
									contact => contact.Id !== contactId,
								),
							},
							tableContacts: prevState.tableContacts.filter(
								contact => contact.Id !== contactId,
							),
						}));

						const { PrimaryContactId, EmergencyContactId, BillingContactId } = form;
						if (
							contactId === PrimaryContactId ||
							contactId === EmergencyContactId ||
							contactId === BillingContactId
						) {
							const newForm = {
								...form,
								PrimaryContactId:
									contactId === PrimaryContactId ? "" : PrimaryContactId,
								EmergencyContactId:
									contactId === EmergencyContactId ? "" : EmergencyContactId,
								BillingContactId:
									contactId === BillingContactId ? "" : BillingContactId,
							};

							this.setState(
								state => ({
									client: { ...state.client, loading: true },
									form: newForm,
								}),
								() => this.updateClient(true),
							);
						}

						utils.notify({
							type: "success",
							message: lang.t("Contact successfully deleted"),
						});
					})
					.catch(error => {
						const { data } = error.response;

						utils.notify({
							type: "error",
							message: data.Message || lang.t("Error deleting contact"),
						});

						this.setState(state => ({
							contacts: { ...state.contacts, loading: false },
						}));
					});
			},
		});
	};

	/**
	 * Handles client form input changes
	 * @param {String} property The property to update
	 */
	onFormChange = property => event => {
		let value = event?.target?.value ?? event?.value;
		if (property === "IsAgencyRKPayEnabled") value = event.target.checked;
		this.setState(state => ({
			form: { ...state.form, [property]: value },
		}));
	};

	onInvoiceTypeChange = invoiceType => {
		this.setState({
			validation: {
				Name: false,
				BillingContactId: false,
			},
		});
		if (invoiceType !== 1) {
			this.setState(state => ({
				form: {
					...state.form,
					JobInvoicedTypeId: invoiceType,
					IsAgencyRKPayEnabled: false,
				},
			}));
		} else {
			this.setState(state => ({
				form: { ...state.form, JobInvoicedTypeId: invoiceType, IsAgencyRKPayEnabled: true },
			}));
		}
	};

	/**
	 * Handles changes to the checkbox that represents `this.state.isAgencyClient`
	 * @param {Event} event The HTML event handled by this method
	 */
	onCheckboxChange = event => {
		const { user } = this.props;
		const {
			target: { checked },
		} = event;
		const { clientCoordinators } = this.state;
		this.setState(prevState => {
			let { ClientCoordinatorIds } = prevState.form;
			if (ClientCoordinatorIds.some(item => item.value === user.Id) && checked) {
				ClientCoordinatorIds = ClientCoordinatorIds.filter(item => item.value !== user.Id);
			} else if (!ClientCoordinatorIds.some(item => item.value === user.Id) && !checked) {
				const currentUser = clientCoordinators.find(item => item.value === user.Id);
				if (currentUser) {
					ClientCoordinatorIds.push(currentUser);
				}
			}
			return {
				form: {
					...prevState.form,
					ClientCoordinatorIds,
				},
				isAgencyClient: checked,
			};
		});
	};

	/**
	 * Handles the Enter key being pressed while focused on a contact record in the Contacts table
	 * @param {Number} contactId The ID number for a contact
	 */
	onRowKeyPress = contactId => event => {
		if (event.key === "Enter") {
			this.onEditContact(null, contactId)(event);
		}
	};

	/**
	 * Filters the list of contacts based on the value of the search input
	 * @param {Event} event The HTMl event handled by this method
	 */
	onSearchChange = event => {
		const {
			target: { value },
		} = event;
		const { tableContacts } = this.state;
		const filteredList = tableContacts.filter(
			item =>
				item.ContactName.toLowerCase().includes(value.toLowerCase()) ||
				item.EmailAddress.toLowerCase().includes(value.toLowerCase()),
		);
		this.setState(prevState => ({
			contacts: {
				...prevState.contacts,
				search: value,
				data: value.trim() === "" ? tableContacts : filteredList,
			},
		}));
	};

	toggleInviteClientModal = () => {
		this.setState(prevState => ({ showInviteClientModal: !prevState.showInviteClientModal }));
	};

	/**
	 * Adds a new user to the list of LEA coordinators permitted to work with the client
	 * @param {Event} event The HTML event handled by this method
	 */
	addClientCoordinator = ({ target: { value } }) => {
		const { clientCoordinators } = this.state;

		const addedClientCoordinator = clientCoordinators.find(
			item => item.value.toString() === value,
		);
		this.setState(prevState => ({
			form: {
				...prevState.form,
				ClientCoordinatorIds: [
					...prevState.form.ClientCoordinatorIds,
					addedClientCoordinator,
				],
			},
		}));
	};

	/**
	 * Removes a user from the list of LEA coordinators permitted to work with the client
	 * @param {*} value Indicates which user to remove from the list
	 */
	removeClientCoordinator = value => {
		this.setState(prevState => {
			const ClientCoordinatorIds = prevState.form.ClientCoordinatorIds.filter(
				item => item.value !== value,
			);
			return {
				form: {
					...prevState.form,
					ClientCoordinatorIds,
				},
				changeCoordinators: ClientCoordinatorIds.length !== 0,
			};
		});
	};

	changeCoordinators = () => {
		this.setState(prevState => ({
			changeCoordinators: !prevState.changeCoordinators,
		}));
	};

	render() {
		const { lang } = global;
		const { user, router } = this.props;
		const {
			editMode,
			client,
			pageTitle,
			contacts,
			ContactId,
			contact,
			form,
			validation,
			TaxExemptionRequest,
			clientCoordinators,
			changeCoordinators,
			isAgencyClient,
			internalCoordinatorIncluded,
			showModal,
			showInviteClientModal,
			agencyRules,
			agencyPaymentRules,
			showAllowExcludeOfficerRate,
			showSchedulingOnlyOptionsModal,
			schedulyOnlyOptions,
		} = this.state;
		// Disabling eslint below because this rule should probably be turned off anyway
		// eslint-disable-next-line no-prototype-builtins
		const isInvoicingV3 = user.Features.some(f => f.toLowerCase() === "invoicingv3");
		const isOfficerInvoicingV3 = user.Features.some(f => f.toLowerCase() === "officerinvoicingv3");
		const manualReconciliationEnabled = user.Organization.ManualReconciliationEnabled;

		const shouldDisableOfficerInvoicing = isOfficerInvoicingV3 &&
			(manualReconciliationEnabled || client.data?.EnforceV2NonRKPayInvoicing);

		return (
			<Fragment>
				<div className="container">
					<div
						className="row"
						id="client-form-header"
					>
						<If condition={editMode}>
							<button
								type="button"
								onClick={this.toggleInviteClientModal}
								className="btn btn-primary pull-right"
							>
								{`+ ${lang.t("Invite to RollKall")}`}
							</button>
						</If>
						<h2>{pageTitle}</h2>
					</div>
					<div
						className="row"
						id="client-form-body"
					>
						<div className="col-md-8 col-md-offset-2 col-sm-12">
							<Tab.Container
								id="client-pills"
								defaultActiveKey="details"
							>
								<div>
									<Nav
										id="client-form-tabs"
										bsStyle="pills"
										className={editMode ? "" : "hidden"}
									>
										<NavItem eventKey="details">
											{lang.t("Client Details")}
										</NavItem>
										<NavItem eventKey="contacts">{lang.t("Contacts")}</NavItem>
									</Nav>
									<Tab.Content animation>
										<Tab.Pane eventKey="details">
											<div id="client-form-fields">
												<div
													className={`row form-group ${validation.Name ? "has-error" : ""}`}
												>
													<div className="col-sm-3 col-xs-12">
														<label htmlFor="Name">{`${lang.t("Client Name")}:`}</label>
													</div>
													<div className="col-sm-7 col-xs-12">
														<input
															type="text"
															name="clientName"
															value={form.Name}
															onChange={this.onFormChange("Name")}
															className="form-control"
														/>
													</div>
												</div>

												<div
													className={`row form-group ${validation.PrimaryContactId ? "has-error" : ""}`}
												>
													<div className="col-sm-3 col-xs-12">
														<label htmlFor="PrimaryContactId">{`${lang.t("Primary Contact")}:`}</label>
													</div>
													<div className="col-sm-7 col-xs-9">
														<ReactSelect
															value={form.PrimaryContactId}
															name="PrimaryContactId"
															onChange={this.onFormChange(
																"PrimaryContactId",
															)}
															options={contacts.options}
															loading={contacts.loading}
															isClearable
														/>
													</div>
													<div className="col-sm-2 col-xs-3 client-form-contact-btn-container">
														<button
															type="button"
															name="PrimaryContactId-add-btn"
															className="btn btn-primary"
															onClick={this.onAddContact}
															title={lang.t("Add Contact")}
														>
															<i className="rkf rkf-plus" />
														</button>
														<If condition={form.PrimaryContactId}>
															<button
																type="button"
																name="PrimaryContactId-edit-btn"
																className="btn btn-primary edit-contact-btn"
																onClick={this.onEditContact(
																	"PrimaryContactId",
																	null,
																)}
																title={lang.t("Edit Contact")}
															>
																<i className="rkf rkf-pencil" />
															</button>
														</If>
													</div>
												</div>

												<div
													className={`row form-group ${validation.EmergencyContactId ? "has-error" : ""}`}
												>
													<div className="col-sm-3 col-xs-12">
														<label htmlFor="EmergencyContactId">{`${lang.t("Emergency Contact")}:`}</label>
													</div>
													<div className="col-sm-7 col-xs-9">
														<ReactSelect
															value={form.EmergencyContactId}
															name="EmergencyContactId"
															onChange={this.onFormChange(
																"EmergencyContactId",
															)}
															options={contacts.options}
															loading={contacts.loading}
															isClearable
														/>
													</div>
													<div className="col-sm-2 col-xs-3 client-form-contact-btn-container">
														<button
															type="button"
															name="EmergencyContactId-add-btn"
															className="btn btn-primary"
															onClick={this.onAddContact}
															title={lang.t("Add Contact")}
														>
															<i className="rkf rkf-plus" />
														</button>
														<If condition={form.EmergencyContactId}>
															<button
																type="button"
																name="EmergencyContactId-edit-btn"
																className="btn btn-primary edit-contact-btn"
																onClick={this.onEditContact(
																	"EmergencyContactId",
																	null,
																)}
																title={lang.t("Edit Contact")}
															>
																<i className="rkf rkf-pencil" />
															</button>
														</If>
													</div>
												</div>

												<div
													className={`row form-group ${validation.BillingContactId ? "has-error" : ""}`}
												>
													<div className="col-sm-3 col-xs-12">
														<label htmlFor="BillingContactId">{`${lang.t("Billing Contact")}:`}</label>
													</div>
													<div className="col-sm-7 col-xs-9">
														<ReactSelect
															value={form.BillingContactId}
															name="BillingContactId"
															onChange={this.onFormChange(
																"BillingContactId",
															)}
															options={contacts.options}
															loading={contacts.loading}
															isClearable
														/>
													</div>
													<div className="col-sm-2 col-xs-3 client-form-contact-btn-container">
														<button
															type="button"
															name="BillingContactId-add-btn"
															className="btn btn-primary"
															onClick={this.onAddContact}
															title={lang.t("Add Contact")}
														>
															<i className="rkf rkf-plus" />
														</button>
														<If condition={form.BillingContactId}>
															<button
																type="button"
																name="BillingContactId-edit-btn"
																className="btn btn-primary edit-contact-btn"
																onClick={this.onEditContact(
																	"BillingContactId",
																	null,
																)}
																title={lang.t("Edit Contact")}
															>
																<i className="rkf rkf-pencil" />
															</button>
														</If>
													</div>
												</div>
												<div className="row form-group">
													<div className="col-sm-3 col-xs-12">
														<label htmlFor="ClientInvoiceDisclaimer">{`${lang.t("Custom Invoice Disclaimer")}:`}</label>
													</div>
													<div className="col-sm-7 col-xs-12">
														<textarea
															className="form-control"
															value={form.ClientInvoiceDisclaimer}
															rows="3"
															placeholder={lang.t(
																"e_g_ Net 15 for all invoices",
															)}
															onChange={this.onFormChange(
																"ClientInvoiceDisclaimer",
															)}
														/>
													</div>
												</div>
												<div className="row form-group">
													<div className="col-sm-3 col-xs-12">
														<label htmlFor="JobInvoicedTypeId">{`${lang.t("Invoicing")}:`}</label>
													</div>
													<div
														className="col-sm-7 col-xs-12"
														id="client-form-toggle"
													>
														<div
															className="radio"
															data-role="Choosing the Officer option for this client will default this client to Officer based invoicing"
															title={shouldDisableOfficerInvoicing ? lang.t("Manually Invoiced clients can't use officer invoicing") : ""}
														>
															<label htmlFor="JobInvoicedTypeId">
																<input
																	type="radio"
																	name="JobInvoicedTypeId"
																	checked={
																		form.JobInvoicedTypeId ===
																		JOB_INVOICED_TYPES.OFFICER_INVOICING
																	}
																	disabled={shouldDisableOfficerInvoicing}
																	onClick={() =>
																		this.onInvoiceTypeChange(
																			JOB_INVOICED_TYPES.OFFICER_INVOICING,
																		)
																	}
																/>
																Officer
															</label>
														</div>
														<If
															condition={
																user.Organization
																	.NonRKPayInvoicingEnabled ||
																user.Organization
																	.NonRKPayPaymentsEnabled ||
																isInvoicingV3
															}
														>
															<div
																className="radio"
																data-role="Choosing the Agency option for this client will default this client to Agency based invoicing"
															>
																<label htmlFor="JobInvoicedTypeId">
																	<input
																		type="radio"
																		name="JobInvoicedTypeId"
																		checked={
																			form.JobInvoicedTypeId ===
																			JOB_INVOICED_TYPES.AGENCY_INVOICING
																		}
																		onClick={() =>
																			this.onInvoiceTypeChange(
																				JOB_INVOICED_TYPES.AGENCY_INVOICING,
																			)
																		}
																	/>
																	Agency
																</label>
															</div>
														</If>
														<div
															className="radio"
															data-role="Choosing the Schedule only option for this client will default this client to Schedule only based jobs"
														>
															<label htmlFor="JobInvoicedTypeId">
																<input
																	type="radio"
																	name="JobInvoicedTypeId"
																	checked={
																		form.JobInvoicedTypeId ===
																		JOB_INVOICED_TYPES.SCHEDULE_ONLY
																	}
																	onClick={() =>
																		this.onInvoiceTypeChange(
																			JOB_INVOICED_TYPES.SCHEDULE_ONLY,
																		)
																	}
																/>
																{lang.t("Scheduling Only")}
															</label>

															<If
																condition={
																	form.JobInvoicedTypeId ===
																		JOB_INVOICED_TYPES.SCHEDULE_ONLY &&
																	isInvoicingV3
																}
															>
																<button
																	type="button"
																	className="btn btn-default btn-xs margin-left-10"
																	onClick={() =>
																		this.setState({
																			showSchedulingOnlyOptionsModal: true,
																		})
																	}
																>
																	<span>
																		<i className="glyphicon glyphicon-save" />
																		&nbsp;{lang.t("Defaults")}
																	</span>
																</button>
															</If>
														</div>
													</div>
												</div>
												<If
													condition={
														agencyPaymentRules.TransferPaymentsToAgencyOption &&
														!agencyPaymentRules.TransferPaymentToJurisdiction
													}
												>
													<div
														id="transfer-payment-to-agency"
														className="row form-group"
													>
														<div className="col-sm-3" />
														<div className="col-sm-7 col-xs-12">
															<label htmlFor="transfer-payments-to-agency">
																<input
																	type="checkbox"
																	name="transfer-payment-agency"
																	id="transfer-payment-agency"
																	checked={
																		form.TransferOfficerPaymentsToAgency
																	}
																	onChange={this.handleTransferOfficerPaymentsToAgency(
																		"TransferOfficerPaymentsToAgency",
																	)}
																/>
																{lang.t(
																	"Transfer payments to agency for this client_s jobs",
																)}
															</label>
														</div>
													</div>
												</If>
												<If condition={editMode}>
													<div className="row form-group">
														<div className="col-sm-3">
															<label htmlFor="tax-exemption">
																{`${lang.t("Tax Exemption")}:`}
															</label>
														</div>
														<div className="col-sm-7">
															<UploadTaxDocument
																taxExemptionRequest={
																	TaxExemptionRequest
																}
																uploadDocument={this.uploadDocument}
															/>
														</div>
													</div>
												</If>

												<If condition={user.isLEA()}>
													<div className="row form-group">
														<div className="col-sm-3 col-xs-12">
															<label htmlFor="OrganizationFee">{`${lang.t("Coordination Fee")}:`}</label>
														</div>
														<div className="col-sm-7 col-xs-12 input-container">
															<input
																className="form-control"
																value={form.OrganizationFee}
																onChange={this.onFormChange(
																	"OrganizationFee",
																)}
															/>
															<p>/ Hour</p>
														</div>
													</div>
												</If>

												<Choose>
													<When condition={user.IsStaffAdmin}>
														<div className="row form-group select-coordinators">
															<div className="col-sm-3 col-xs-12">
																<label htmlFor="Coordinators">{`${lang.t("Coordinators")}:`}</label>
															</div>
															<div className="col-sm-7 col-xs-12">
																<Choose>
																	<When
																		condition={
																			form
																				.ClientCoordinatorIds
																				.length > 0 ||
																			changeCoordinators
																		}
																	>
																		<Choose>
																			<When
																				condition={
																					changeCoordinators
																				}
																			>
																				<SelectBadges
																					id="select-clientCoordinators"
																					defaultOption={lang.t(
																						"Add Coordinator",
																					)}
																					optionList={
																						clientCoordinators
																					}
																					storedOptionList={
																						form.ClientCoordinatorIds
																					}
																					handleOnChange={
																						this
																							.addClientCoordinator
																					}
																					handleOnDelete={
																						this
																							.removeClientCoordinator
																					}
																					editDone={() =>
																						this.setState(
																							{
																								changeCoordinators: false,
																							},
																						)
																					}
																					editPermission={
																						user.IsStaffAdmin
																					}
																				/>
																			</When>
																			<Otherwise>
																				<For
																					each="item"
																					of={
																						form.ClientCoordinatorIds
																					}
																					index="index"
																				>
																					<span
																						className="badge coordinators-badge"
																						key={
																							item.value
																						}
																					>
																						{item.label}
																					</span>
																				</For>
																				<button
																					type="button"
																					className="btn btn-link"
																					onClick={
																						this
																							.changeCoordinators
																					}
																				>
																					<i className="glyphicon glyphicon-pencil" />
																					&nbsp;
																					{lang.t("Edit")}
																				</button>
																			</Otherwise>
																		</Choose>
																	</When>
																	<Otherwise>
																		<span className="badge coordinators-badge">
																			{lang.t(
																				"All (Agency Client)",
																			)}
																		</span>
																		<button
																			type="button"
																			className={
																				!changeCoordinators
																					? "btn btn-link"
																					: "hidden"
																			}
																			onClick={
																				this
																					.changeCoordinators
																			}
																		>
																			<i className="glyphicon glyphicon-pencil" />
																			&nbsp;
																			{lang.t("Change")}
																		</button>
																	</Otherwise>
																</Choose>
															</div>
														</div>
													</When>
													<When
														condition={
															user.IsInternalCoordinator &&
															internalCoordinatorIncluded
														}
													>
														<div className="row form-group">
															<div className="col-sm-3">
																<label htmlFor="Agency Client">{`${lang.t("Agency Client")}:`}</label>
															</div>
															<div className="col-sm-8">
																<div className="checkbox">
																	<label htmlFor="isAgencyClient">
																		<input
																			type="checkbox"
																			id="isAgencyClient"
																			name="isAgencyClient"
																			checked={isAgencyClient}
																			onChange={
																				this
																					.onCheckboxChange
																			}
																		/>
																	</label>
																</div>
															</div>
														</div>
													</When>
												</Choose>

												<div className="row form-group">
													<div className="col-sm-3 col-xs-12">
														<label htmlFor="IsActive">{`${lang.t("Status")}:`}</label>
													</div>
													<div
														className="col-sm-7 col-xs-12"
														id="client-form-is-active"
													>
														<div className="radio">
															<label htmlFor="IsActive_true">
																<input
																	type="radio"
																	name="IsActive"
																	value="true"
																	id="IsActive_true"
																	onChange={this.onFormChange(
																		"IsActive",
																	)}
																	checked={
																		form.IsActive === "true"
																	}
																/>
																{lang.t("Active")}
															</label>
														</div>
														<div className="radio">
															<label htmlFor="IsActive_false">
																<input
																	type="radio"
																	name="IsActive"
																	value="false"
																	id="IsActive_false"
																	onChange={this.onFormChange(
																		"IsActive",
																	)}
																	checked={
																		form.IsActive === "false"
																	}
																/>
																{lang.t("Inactive")}
															</label>
														</div>
													</div>
												</div>
											</div>

											<If condition={editMode}>
												<div className="row form-group client-documents">
													<div className="col-sm-3 col-xs-12">
														<label htmlFor="Documents">{`${lang.t("Documents")}:`}</label>
													</div>
													<div className="col-sm-7 col-xs-12">
														<If
															condition={
																form.clientDocuments.length > 0
															}
														>
															<ul className="list-unstyled">
																<For
																	each="item"
																	of={form.clientDocuments}
																	index="index"
																>
																	<li
																		key={`client-document_${item.Id}`}
																	>
																		{utils.formatDate(
																			new Date(
																				`${item.CreateDateUTC}Z`,
																			),
																			"en-US",
																			user.TimeZoneId,
																			"2-digit",
																		)}
																		<a
																			href={item.Url}
																			rel="noopener noreferrer"
																			target="_blank"
																			aria-label="View Document"
																		>
																			{item.FileName}
																		</a>
																		<button
																			type="button"
																			className="btn btn-link"
																			onClick={() =>
																				this.onDeleteDocument(
																					item,
																				)
																			}
																		>
																			<i className="glyphicon glyphicon-trash" />
																		</button>
																	</li>
																</For>
															</ul>
														</If>
														<div className="button-container">
															<input
																type="file"
																ref={ref => {
																	this.clientDocInput = ref;
																}}
																accept=".pdf,.jpeg,.jpg,.png,"
																onChange={this.uploadClientDocument}
																className="hidden"
															/>
															<button
																id="client-document-button"
																type="button"
																className="btn btn-default"
																onClick={this.clientDocumentClick}
															>
																{lang.t("Upload Document")}
															</button>
															<em>{`(${lang.t("max size 100MB")})`}</em>
														</div>
													</div>
												</div>
											</If>
											<If
												condition={
													agencyRules.OnlyAuthorizedClientsAreAllowedToUseVehicles &&
													user.isLEA()
												}
											>
												<div
													id="manage-vehicles"
													className="row form-group"
												>
													<div className="col-sm-3 col-xs-12">
														<label htmlFor="IsActive">{`${lang.t("Vehicles")}:`}</label>
													</div>
													<div className="col-sm-7 col-xs-12">
														<label htmlFor="only-authorized-clients-vehicles">
															<input
																type="checkbox"
																name="authorized-to-use-vehicle"
																id="authorized-to-use-vehicle"
																checked={
																	form.AuthorizedToUseDepartmentVehicles
																}
																onChange={this.handleAuthorizedToUseDepartmentVehicles(
																	"AuthorizedToUseDepartmentVehicles",
																)}
															/>
															{lang.t(
																"The client is authorized to use department vehicles",
															)}
														</label>
														<If
															condition={
																agencyRules.RequireInvoicingForVehicles &&
																user.Organization
																	.VehicleInvoicingEnabled &&
																showAllowExcludeOfficerRate
															}
														>
															<label htmlFor="require-vehicle-invoicing">
																<input
																	type="checkbox"
																	name="require-vehicle-invoicing"
																	id="require-vehicle-invoicing"
																	checked={
																		form.AllowExcludeOfficerRate
																	}
																	onChange={this.handleAllowExcludeOfficerRate(
																		"AllowExcludeOfficerRate",
																	)}
																/>
																{lang.t(
																	"Exclude officer pay for required vehicle invoicing",
																)}
															</label>
															<If
																condition={
																	client?.data
																		?.EnforceIncludeOfficerRate
																}
															>
																<div className="alert bg-warning text-left">
																	<i className="glyphicon glyphicon-info-sign" />
																	&nbsp;
																	{lang.t(
																		"This client has opted in to having all invoices include Officer Pay",
																	)}
																</div>
															</If>
														</If>
													</div>
												</div>
											</If>

											<div
												className="row"
												id="client-form-btn-container"
											>
												<button
													type="button"
													className="btn btn-primary col-sm-2"
													onClick={this.onSaveClick}
												>
													{lang.t("Save")}
												</button>
												<button
													type="button"
													className="btn btn-link col-sm-2"
													onClick={() => router.push("/clients")}
												>
													{lang.t("Cancel")}
												</button>
											</div>
										</Tab.Pane>
										<Tab.Pane eventKey="contacts">
											<div>
												<div>
													<h2>{lang.t("Staff")}</h2>
													<div
														id="client-form-submenu"
														className="row"
													>
														<div className="col-xs-8">
															<SearchField
																placeholder={lang.t(
																	"Search by Name",
																)}
																onChange={this.onSearchChange}
															/>
														</div>
														<div className="col-xs-4 text-right">
															<button
																type="button"
																id="add-new-staff"
																onClick={this.onAddContact}
																className="btn btn-primary"
															>
																{`+ ${lang.t("Add")}`}
															</button>
														</div>
													</div>
												</div>
												<div
													id="client-form-table-header"
													className="row hidden-xs"
												>
													<div className="col-xs-3">
														<strong>{lang.t("Name")}</strong>
													</div>
													<div className="col-xs-3">
														<strong>{lang.t("Phone")}</strong>
													</div>
													<div className="col-xs-4">
														<strong>{lang.t("EMail")}</strong>
													</div>
													<div className="col-xs-2 text-right">
														<strong>{lang.t("Actions")}</strong>
													</div>
												</div>
												<div className="row">
													<div
														id="client-form-table-body"
														className="col-xs-12"
													>
														<For
															each="item"
															index="index"
															of={contacts.data}
														>
															<div
																role="row"
																tabIndex="0"
																className="row"
																key={`client_form_contact_item_${index}`}
																onClick={this.onEditContact(
																	null,
																	item.Id,
																)}
																onKeyPress={this.onRowKeyPress(
																	item.Id,
																)}
															>
																<div className="col-sm-3 col-xs-12">
																	<strong>
																		{item.ContactName}
																	</strong>
																</div>

																<div className="col-sm-3 col-xs-12">
																	<span className="visible-xs-inline-block">
																		{`${lang.t("Phone")}:`}
																		&nbsp;
																	</span>
																	<a
																		href={`tel:${utils.formatPhoneNumber(item.MobilePhone)}`}
																		data-prevent-navigation
																	>
																		{utils.formatPhoneNumber(
																			item.MobilePhone,
																		)}
																	</a>
																</div>

																<div
																	className="col-sm-4 col-xs-12 truncate"
																	title={item.EmailAddress}
																>
																	<span className="visible-xs-inline-block">
																		{`${lang.t("EMail")}:`}
																		&nbsp;
																	</span>
																	<a
																		href={`mailto:${item.EmailAddress}`}
																		data-prevent-navigation
																	>
																		{item.EmailAddress}
																	</a>
																</div>

																<div className="col-sm-2 col-xs-12 text-right">
																	<button
																		type="button"
																		id="client-form-delete-contact"
																		aria-label={lang.t(
																			"Delete Contact",
																		)}
																		onClick={this.onDeleteContact(
																			item.Id,
																		)}
																		className="btn btn-primary"
																		title={lang.t(
																			"Delete Contact",
																		)}
																		data-prevent-navigation
																	>
																		{lang.t("Delete")}
																	</button>
																</div>
															</div>
														</For>
													</div>
												</div>
											</div>
										</Tab.Pane>
									</Tab.Content>
								</div>
							</Tab.Container>
						</div>
						<Loader active={client.loading} />
					</div>
				</div>
				<AddContactModal
					contactId={ContactId}
					client={client.data}
					clientId={client.data.Id}
					closeModal={this.onCloseContact}
					contact={contact}
					user={user}
					showModal={showModal}
					updateOnClose={editMode}
					onSubmit={!editMode ? this.updateContactsForNewClient : undefined}
					submittedEvent={editMode ? this.updateContactsForExistingClient : undefined}
					getContacts={editMode ? this.getContacts : undefined}
					updateClient={this.getClient}
				/>
				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>
				<InviteClientModal
					show={showInviteClientModal}
					onHide={this.toggleInviteClientModal}
					onSuccess={this.toggleInviteClientModal}
					clientId={client.data.Id}
				/>
				<SchedulingOnlyOptionsModal
					clientId={client.data.Id}
					show={showSchedulingOnlyOptionsModal}
					onClose={() => this.setState({ showSchedulingOnlyOptionsModal: false })}
					onSave={options => this.setState({ schedulyOnlyOptions: options })}
					options={contacts.options}
					onAddContact={this.onAddContact}
					schedulyOnlyOptions={schedulyOnlyOptions}
					enforceV2NonRKPayInvoicing={client.data?.EnforceV2NonRKPayInvoicing}
				/>
			</Fragment>
		);
	}
}

ClientForm.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object.isRequired,
	flags: PropTypes.object.isRequired,
};

ClientForm.defaultProps = {
	user: {},
};

export default withLDConsumer()(ClientForm);
